import React, { useRef, useState } from "react";
import { has } from "lodash";
import { useDrag, useDrop } from "react-dnd";
import AppConstants from "../../constants/AppConstants";
import {
  generateDragSpec,
  generateDropSpec,
  getTargetArea,
} from "../../utils/DnDUtils";
import { galleryAssetPanelDrop } from "./AssetDropHandlers";
import AssetUtils from "../../utils/AssetUtils";
import AsyncPreviewImage from "./AsyncPreviewImage";
import styles from "./AsyncPreviewImage.module.css";
import {
  ASSET_ITEM,
  GALLERY_ASSET,
  GALLERY_ASSET_LIST_ITEM,
} from "../../constants/DnDConstants";

const { DROP_BORDER } = AppConstants;

const itemStyleList = {
  display: "flex",
  height: 50,
  justifyContent: "flex-start",
  paddingBottom: "6px",
};

const itemStyleGrid = {
  display: "flex",
  height: "100%",
  width: "100%",
  flexDirection: "column",
  padding: "4px",
};

const labelStyle = {
  textAlign: "center",
  padding: 12,
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflow: "hidden",
};

const GalleryAssetListItem = ({
  gridMode = false,
  index,
  onMoveImage,
  onMouseDown,
  onMouseUp,
  asset,
}) => {
  const [dropArea, setDropArea] = useState(null);
  const selfRef = useRef();
  const asyncPreview = AssetUtils.hasAsyncPreview(asset);

  const drag = generateDragSpec({
    type: GALLERY_ASSET_LIST_ITEM,
    item: {
      index,
      gridMode,
      type: GALLERY_ASSET,
    },
    onDrop: ({ data }) => onMoveImage(data),
  });

  const drop = generateDropSpec({
    type: [ASSET_ITEM, GALLERY_ASSET_LIST_ITEM],
    onHover: (monitor) => setDropArea(getTargetArea(monitor, selfRef.current)),
    onDrop: galleryAssetPanelDrop({ dropRef: selfRef.current, index }),
  });

  const [{ isDragging }, dragRef] = useDrag(drag);
  const [{ isOver }, dropRef] = useDrop(drop);

  const headingStyle = { margin: "2px" };

  if (has(asset, "selected")) {
    headingStyle.outline = "var(--background-accent-primary) 2px solid";
    headingStyle.borderRadius = "4px";
  }

  if (isOver && !isDragging) {
    const borderSide = {
      bottom: "borderBottom",
      left: "borderLeft",
      right: "borderRight",
      top: "borderTop",
    }[dropArea];
    borderSide && (headingStyle[borderSide] = DROP_BORDER);
  }

  const contentStyle = {};
  if (!asyncPreview) {
    contentStyle.backgroundImage = `url(${AssetUtils.getV2PreviewURL(asset.key, {
      provider: asset.provider,
    })})`;
    contentStyle.backgroundPosition = "50% 50%";
    contentStyle.backgroundRepeat = "no-repeat";
  }

  if (gridMode) {
    headingStyle.height = "130px";
    headingStyle.width = "130px";

    if (!asyncPreview) {
      contentStyle.backgroundSize = "contain";
    }
    contentStyle.width = "100%";
    contentStyle.height = "100%";
  } else {
    headingStyle.height = "50px";

    if (!asyncPreview) {
      contentStyle.backgroundSize = "cover";
    }
    contentStyle.flex = "0 0 50px";
  }

  return (
    <div
      className="gallery-asset-item"
      style={headingStyle}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      ref={(self) => {
        selfRef.current = self;
        dragRef(self);
        dropRef(self);
      }}
    >
      <div style={gridMode ? itemStyleGrid : itemStyleList}>
        <div
          className={asyncPreview ? styles.thumbnail : undefined}
          style={contentStyle}
        >
          {asyncPreview && (
            <AsyncPreviewImage
              asset={asset}
              alt={asset.title}
              objectFit={gridMode ? "contain" : "cover"}
            />
          )}
        </div>
        <div style={labelStyle} title={asset.title}>
          {asset.title}
        </div>
      </div>
    </div>
  );
};

export default GalleryAssetListItem;
